import type { CSSProperties } from 'react'
import styled, { keyframes } from 'styled-components'

/** The colours of Figma "Confetti 1" (3041:28703). */
const COLORS = ['#ff4d4f', '#ff5fa2', '#3fbf5f', '#2f7bff', '#9b4dff', '#ffc531', '#ff8a3d']

/** Where each firework explodes (% of the box) and when its rocket leaves the bottom, in s. */
const FIREWORKS = [
  { x: 28, y: 36, launch: 0, color: '#ff4d4f' },
  { x: 72, y: 28, launch: 0.9, color: '#2f7bff' },
  { x: 48, y: 58, launch: 1.8, color: '#ffc531' },
  { x: 80, y: 62, launch: 2.6, color: '#9b4dff' },
]
/** How long a rocket climbs before it explodes, in s. */
const RISE = 0.85
const SPARKS_PER_BURST = 24

/**
 * Seeded, so the server and the browser draw the same show (Math.random would break
 * hydration) and every visit sees the same one.
 */
const seeded = (seed: number) => () => {
  seed = (seed + 0x6d2b79f5) | 0
  let n = Math.imul(seed ^ (seed >>> 15), 1 | seed)
  n = (n + Math.imul(n ^ (n >>> 7), 61 | n)) ^ n
  return ((n ^ (n >>> 14)) >>> 0) / 4294967296
}

interface Firework {
  rocket: CSSProperties
  sparks: CSSProperties[]
}

const makeShow = (): Firework[] => {
  const random = seeded(7)
  return FIREWORKS.map((firework, fireworkIndex) => {
    const position = { '--x': `${firework.x}%`, '--y': `${firework.y}%` }
    const burstAt = firework.launch + RISE
    return {
      rocket: {
        ...position,
        // Unitless: the climb is measured in container units from the bottom edge.
        '--from': 100 - firework.y,
        '--color': firework.color,
        '--delay': `${firework.launch}s`,
        '--rise': `${RISE}s`,
        '--burst-delay': `${burstAt}s`,
      } as CSSProperties,
      sparks: Array.from({ length: SPARKS_PER_BURST }, (_, index) => {
        // Evenly round the circle, jittered, so a burst reads as a ring rather than a clump.
        const angle = ((index + random() * 0.6) / SPARKS_PER_BURST) * Math.PI * 2
        const distance = 55 + random() * 70
        const round = random() < 0.3
        return {
          ...position,
          '--dx': `${(Math.cos(angle) * distance).toFixed(1)}px`,
          '--dy': `${(Math.sin(angle) * distance).toFixed(1)}px`,
          '--fall': `${Math.round(60 + random() * 70)}px`,
          '--w': round ? '6px' : `${(5 + random() * 4).toFixed(1)}px`,
          '--h': round ? '6px' : `${(3 + random() * 2).toFixed(1)}px`,
          '--radius': round ? '50%' : '1px',
          '--spin': `${Math.round(240 + random() * 480) * (random() < 0.5 ? -1 : 1)}deg`,
          '--duration': `${(2.8 + random() * 0.9).toFixed(2)}s`,
          '--delay': `${(burstAt + random() * 0.06).toFixed(2)}s`,
          // Mostly the rocket's colour, with a few others mixed in.
          '--color': random() < 0.6 ? firework.color : COLORS[(index + fireworkIndex * 3) % COLORS.length],
        } as CSSProperties
      }),
    }
  })
}

const SHOW = makeShow()

/** The rocket climbs from the bottom edge, slowing near the top as a real one does. */
const climb = keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, calc(var(--from) * 1cqh));
  }
  8% {
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate(-50%, 0);
  }
`

/** A short flash where the rocket explodes. */
const flash = keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.2);
  }
  20% {
    opacity: 0.9;
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(1.6);
  }
`

/** The burst: fast out from the centre, easing to a stop as the sparks lose speed. */
const shoot = keyframes`
  from {
    transform: translate(0, 0) scale(0.2);
  }
  35% {
    transform: translate(var(--dx), var(--dy)) scale(1);
  }
  to {
    transform: translate(var(--dx), var(--dy)) scale(1);
  }
`

/** Then gravity: a slow start that gathers pace, tumbling and fading out. */
const drop = keyframes`
  /* Hidden until its burst goes off: the first frame also holds through the delay. */
  0% {
    opacity: 0;
    transform: translateY(0) rotate(0deg);
  }
  3% {
    opacity: 1;
  }
  30% {
    opacity: 1;
    transform: translateY(0) rotate(0deg);
  }
  75% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateY(var(--fall)) rotate(var(--spin));
  }
`

const Layer = styled.div`
  container-type: size;
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`

/** A bright head with a fading trail below it. */
const Rocket = styled.span`
  position: absolute;
  left: var(--x);
  top: var(--y);
  width: 3px;
  height: 22px;
  border-radius: 2px;
  background: linear-gradient(to top, transparent, var(--color));
  opacity: 0;
  animation: ${climb} var(--rise) cubic-bezier(0.25, 0.6, 0.4, 1) var(--delay) both;
  will-change: transform, opacity;
`

const Flash = styled.span`
  position: absolute;
  left: var(--x);
  top: var(--y);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: radial-gradient(circle, #ffffff 0%, var(--color) 35%, transparent 70%);
  opacity: 0;
  animation: ${flash} 0.45s ease-out var(--burst-delay) both;
  will-change: transform, opacity;
`

const Spark = styled.span`
  position: absolute;
  left: var(--x);
  top: var(--y);
  animation: ${shoot} var(--duration) cubic-bezier(0.1, 0.75, 0.25, 1) var(--delay) both;
  will-change: transform;
`

const Bit = styled.span`
  display: block;
  width: var(--w);
  height: var(--h);
  margin: calc(var(--h) / -2) 0 0 calc(var(--w) / -2);
  border-radius: var(--radius);
  background: var(--color);
  opacity: 0;
  animation: ${drop} var(--duration) cubic-bezier(0.55, 0, 0.9, 0.6) var(--delay) both;
  will-change: transform, opacity;
`

/**
 * A CSS-only fireworks show over its positioned parent: rockets climb from the bottom edge
 * and explode into confetti that falls away. Plays once each time it mounts (about 7s), then
 * is gone; hidden for reduced motion.
 */
export const Fireworks = ({ className }: { className?: string }) => (
  <Layer className={className} aria-hidden="true">
    {SHOW.map(({ rocket, sparks }, fireworkIndex) => (
      <span key={fireworkIndex}>
        <Rocket style={rocket} />
        <Flash style={rocket} />
        {sparks.map((style, index) => (
          <Spark key={index} style={style}>
            <Bit />
          </Spark>
        ))}
      </span>
    ))}
  </Layer>
)
